import classNames from "classnames";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { openCookieSettings } from "../../../utils/cookieConsent";
import { FRONT_LINKS, FRONT_SOCIAL_LINKS } from "../../../utils/frontConstants";
import { useSiteLink } from "../../../utils/useSiteLink";
import { Clickable } from "../../button/Clickable";
import { Icon } from "../../graphic/Icon";
import { ShieldLogo } from "../../graphic/ShieldLogo";
import styles from "./SiteFooter.scss";

const msgKey = "siteFooter";

const COLUMNS = [
    {
        id: "product",
        links: [
            { id: "pricing", link: FRONT_LINKS.pricing },
            { id: "startFreeTrial", link: FRONT_LINKS.signup },
            { id: "bookDemo", link: FRONT_LINKS.contact },
            { id: "logIn", link: FRONT_LINKS.login }
        ]
    },
    {
        id: "resources",
        links: [
            { id: "blog", href: FRONT_LINKS.blog },
            { id: "courses", link: FRONT_LINKS.courses }
        ]
    },
    {
        id: "company",
        links: [
            { id: "contact", link: FRONT_LINKS.contact },
            { id: "privacyPolicy", link: FRONT_LINKS.privacy },
            { id: "terms", link: FRONT_LINKS.terms },
            { id: "cookieSettings", onClick: openCookieSettings }
        ]
    }
];

/**
 * Footer for the public pages: a small shield mark, the link columns, and our social profiles and copyright at the
 * bottom
 */
export const SiteFooter = ({ className }) => {
    const siteLink = useSiteLink();
    const intl = useIntl();

    return (
        <footer className={classNames(styles.base, className)}>
            <div className={styles.top}>
                <Link
                    to={siteLink("/")}
                    className={styles.brand}
                    aria-label={intl.formatMessage({ id: `${msgKey}.home` })}
                >
                    <ShieldLogo />
                </Link>
                <div className={styles.columns}>
                    {COLUMNS.map((column) => (
                        <nav key={column.id} aria-label={intl.formatMessage({ id: `${msgKey}.${column.id}` })}>
                            <p className={styles.heading}>
                                <FormattedMessage id={`${msgKey}.${column.id}`} />
                            </p>
                            <ul className={styles.links}>
                                {column.links.map(({ id, link, href, onClick }) => (
                                    <li key={id}>
                                        {onClick ? (
                                            <button
                                                type="button"
                                                className={classNames(styles.link, styles.button)}
                                                onClick={onClick}
                                            >
                                                <FormattedMessage id={`${msgKey}.${id}`} />
                                            </button>
                                        ) : (
                                            <Clickable
                                                link={link && siteLink(link)}
                                                href={href}
                                                className={styles.link}
                                            >
                                                <FormattedMessage id={`${msgKey}.${id}`} />
                                            </Clickable>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    ))}
                </div>
            </div>
            <div className={styles.bottom}>
                <ul className={styles.social}>
                    {FRONT_SOCIAL_LINKS.map(({ id, icon, href }) => (
                        <li key={id}>
                            <a
                                href={href}
                                target="_blank"
                                rel="noreferrer"
                                className={styles.socialLink}
                                aria-label={intl.formatMessage({ id: `${msgKey}.${id}` })}
                                title={intl.formatMessage({ id: `${msgKey}.${id}` })}
                            >
                                <Icon name={icon} />
                            </a>
                        </li>
                    ))}
                </ul>
                <span>
                    <FormattedMessage id={`${msgKey}.copyright`} values={{ year: new Date().getFullYear() }} />
                </span>
            </div>
        </footer>
    );
};
